<script lang="ts" setup>
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { BPTQuestion, questionTypes, textRules } from '@/models/bpt.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { BPTStoreActions, BPTStoreState } from '@/stores/bpt.store';
import BPTAnswersField from '@/components/bpt/BPTAnswersField.vue';

const store = useStore<ShiftCMSState & { bpt: BPTStoreState }>();
const questions = computed<BPTQuestion[]>(() => store.state.bpt.questions);

const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<BPTQuestion>,
  },
});

const valid = ref<boolean>(false);

const question = ref<BPTQuestion>({
  id: NEW_ID,
  text: '',
  type: '',
  index: 0,
  onboarding: false,
  answers: [],
  ...(props.data || {}),
});

const action = computed<string>(() =>
  question.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveBPTQuestion = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (question.value.id === NEW_ID) {
      success = await store.dispatch(
        BPTStoreActions.AddBPTQuestion,
        question.value,
      );
    } else {
      success = await store.dispatch(
        BPTStoreActions.UpdateBPTQuestion,
        question.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved question',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(BPTStoreActions.GetBPTQuestions);
  }
};

const updateLanguage = async () => {
  if (question.value.id === NEW_ID) {
    Object.assign(question.value, { title: '', type: 'question' });
  } else {
    const q = await store.dispatch(
      BPTStoreActions.GetBPTQuestion,
      question.value.id,
    );

    if (q) {
      Object.assign(question.value, { ...q });
    }
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  if (props.data) {
    Object.assign(question.value, props.data);
  }
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

const questionTypesFiltered = computed(() =>
  questionTypes.filter(
    (v) => !questions.value.map((q) => q.type).includes(v.value),
  ),
);
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveBPTQuestion">
    <v-container>
      <h2>{{ $t(action) }} a question <CurrentLanguage /></h2>

      <v-text-field
        v-model="question.text"
        :rules="textRules"
        label="Title"
        required
      ></v-text-field>

      <v-row>
        <v-col cols="8">
          <v-select
            label="Type"
            v-model="question.type"
            hint="The type of the question will provide the values for the answers."
            :items="questionTypesFiltered"
          />
        </v-col>
        <v-col>
          <v-checkbox v-model="question.onboarding" label="Use in onboarding" />
        </v-col>
      </v-row>

      <v-alert type="info" v-if="question.type === 'interests'"
        >The interests question should not have answers.</v-alert
      >

      <v-alert type="info" v-if="question.type === 'location'"
        >The location question should not have answers.</v-alert
      >

      <BPTAnswersField
        v-model="question.answers"
        :question-type="question.type"
      />

      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
.calculation {
  background-color: rgb(179, 227, 255);
  padding: 16px;
  border-radius: 16px;
  margin: 16px 0;
  box-shadow: 2px 2px 4px silver;
}
.calculation-field {
  margin-bottom: 16px;
}
</style>
